/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { ModalScrollScreen } from '../../shared/layout/ModalScrollScreen';
import { usePlanEntitlements } from '../../shell/providers/PlanEntitlementsProvider';
import { useGlobalFeatureFlags } from '../../shell/providers/GlobalFeatureFlagsProvider';
import { isGlobalFeatureOn } from '../../shell/feature-flags/globalFeatureFlagsService';
import { useBusinessProfileStore } from '../../shell/session/store/useBusinessProfileStore';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import { fetchBusinessTypes } from '../../onboarding/application/businessTypeService';
import { filterBusinessTypesForOnboardingPicker } from '../../onboarding/application/onboardingBusinessTypeVisibility';
import {
  expandBusinessTypesToPickerItems,
  type OnboardingPickerItem,
} from '../../onboarding/application/onboardingPickerService';
import {
  DEFAULT_OTHER_FEATURE_GROUPS,
  resolveTemplateFromOtherFeatures,
  toggleOtherFeature,
  type OtherFeatureOptionId,
} from '../../onboarding/application/otherFeatureCatalog';
import {
  changeOrganizationBusinessType,
  fetchBusinessTypeChangeBlockers,
} from '../../onboarding/application/businessTypeChangeService';
import {
  businessTypeChangeBlockersClear,
  type BusinessTypeChangeBlockers,
} from '../../onboarding/application/businessTypeChangeUtils';
import type { BusinessTypeCode } from '../../shell/session/domain/businessProfile';
import { resolveOrganizationRubroDisplayLabel } from '../../shell/session/domain/businessProfile';
import {
  isOtherRubroPickerCode,
  OTHER_RUBRO_PICKER_CODE,
  resolveBusinessTypeFromOtherTemplate,
  resolveBusinessTypeFromLabelAndTemplate,
} from '../../onboarding/application/otherRubroFlow';
import { createOtherRubroSupportTicketIfNeeded } from '../../onboarding/application/otherRubroSupportTicket';
import { applyOnboardingFeatureOverrides } from '../../onboarding/application/onboardingFeatureOverrides';
import { useAuthStore } from '../../auth/store/useAuthStore';
import { BusinessTypePickerGrid } from '../../auth/screens/onboarding/BusinessTypePickerGrid';
import { useOnboardingLayout } from '../../auth/screens/onboarding/useOnboardingLayout';
import { OtherRubroStepPanel } from '../../auth/screens/onboarding/OtherRubroStepPanel';
import { BusinessTypeChangeImpactPanel } from './components/BusinessTypeChangeImpactPanel';
import { spacing } from '../../shared/theme/spacing'
import { typography } from '../../shared/theme/typography';
import { showError, showSuccess } from '../../shared/ui/appToast';
import { getErrorMessage } from '../../shared/errorMessage';
import { presentUnexpectedFailure } from '../../shared/sentry/presentUnexpectedFailure';
import { navigateInShell } from '../../shell/navigation/rootNavigation';
import { useScreenLoadShell } from '../../shared/ui/hooks-screen/useScreenLoad';
import { ModalHeroHeader } from '../../shared/modals/ModalHeroHeader';
import { ticketColors } from '../../shared/theme/ticketColors';

type Step = 'pick' | 'other-rubro' | 'review';

export const ChangeBusinessTypeScreen = () => {
  const navigation = useNavigation<any>();
  const layout = useOnboardingLayout();
  const businessProfile = useBusinessProfileStore((state) => state.profile);
  const { entitlements, loading: entLoading } = usePlanEntitlements();
  const { flags } = useGlobalFeatureFlags();
  const multiBusinessEnabled = isGlobalFeatureOn(flags, 'multi_business_profiles_enabled');

  const [step, setStep] = useState<Step>('pick');
  const [pickerItems, setPickerItems] = useState<OnboardingPickerItem[]>([]);
  const [loadingTypes, setLoadingTypes] = useState(true);
  const [selectedPickerKey, setSelectedPickerKey] = useState<string | null>(null);
  const [understood, setUnderstood] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [otherFeatureIds, setOtherFeatureIds] = useState<OtherFeatureOptionId[]>([]);
  const [otherRubroLabel, setOtherRubroLabel] = useState('');
  const [blockers, setBlockers] = useState<BusinessTypeChangeBlockers | null>(null);
  const [blockersLoading, setBlockersLoading] = useState(false);

  const currentCode = businessProfile?.business_type_code ?? 'retail_counter';
  const currentLabel = resolveOrganizationRubroDisplayLabel(businessProfile, pickerItems);
  const selectedItem = useMemo(
    () => pickerItems.find((t) => t.picker_key === selectedPickerKey) ?? null,
    [pickerItems, selectedPickerKey],
  );
  const selectedCode = selectedItem?.code ?? null;
  const enteredViaOtherRubro = isOtherRubroPickerCode(selectedCode);
  const otherRubroTemplate = useMemo(
    () => (enteredViaOtherRubro ? resolveTemplateFromOtherFeatures(otherFeatureIds) : null),
    [enteredViaOtherRubro, otherFeatureIds],
  );
  const resolvedTargetCode = useMemo(() => {
    if (!enteredViaOtherRubro || !otherRubroTemplate) {
      return selectedCode;
    }
    return resolveBusinessTypeFromOtherTemplate(otherRubroTemplate);
  }, [enteredViaOtherRubro, otherRubroTemplate, selectedCode]);

  const loadTypes = useCallback(async () => {
    if (!multiBusinessEnabled) return;
    setLoadingTypes(true);
    try {
      const types = await fetchBusinessTypes('AR');
      setPickerItems(expandBusinessTypesToPickerItems(filterBusinessTypesForOnboardingPicker(types)));
    } catch (e) {
      console.warn('[ChangeBusinessType] list_business_types', e);
      const visibleTypes = expandBusinessTypesToPickerItems(filterBusinessTypesForOnboardingPicker([]));
      setPickerItems(visibleTypes);
      if (visibleTypes.length > 0) {
        showError(
          'Lista de rubros limitada',
          'No pudimos actualizar desde el servidor. Mostramos Kiosco y Mercado.',
        );
      } else {
        showError('No se pudieron cargar los rubros', 'Probá de nuevo más tarde.');
        presentUnexpectedFailure({ module: 'settings', op: 'change_business_type.load_types', error: e });
      }
    } finally {
      setLoadingTypes(false);
    }
  }, [multiBusinessEnabled]);

  const loadBlockers = useCallback(async () => {
    if (!multiBusinessEnabled || entitlements?.is_owner !== true) return;
    setBlockersLoading(true);
    try {
      const next = await fetchBusinessTypeChangeBlockers();
      setBlockers(next);
    } catch (e) {
      console.warn('[ChangeBusinessType] blockers', e);
      setBlockers(null);
      showError('No se pudo chequear', 'Cerrá caja, pedidos y turnos y volvé a intentar.');
      presentUnexpectedFailure({ module: 'settings', op: 'change_business_type.load_blockers', error: e });
    } finally {
      setBlockersLoading(false);
    }
  }, [multiBusinessEnabled, entitlements?.is_owner]);

  useEffect(() => {
    void loadTypes();
  }, [loadTypes]);

  const { refreshing, onRefresh } = useScreenLoadShell(
    'ChangeBusinessType',
    async () => {
      await loadTypes();
      await loadBlockers();
    },
    { refreshOnFocus: false },
  );

  useFocusEffect(
    useCallback(() => {
      if (step === 'review') {
        void loadBlockers();
      }
    }, [step, loadBlockers]),
  );

  const selectedMeta = selectedItem;
  const resolvedMeta = useMemo(
    () =>
      resolvedTargetCode
        ? pickerItems.find((t) => t.code === resolvedTargetCode && !t.vertical_flavor) ??
          pickerItems.find((t) => t.code === resolvedTargetCode) ??
          null
        : null,
    [pickerItems, resolvedTargetCode],
  );
  useEffect(() => {
    setUnderstood(false);
  }, [selectedPickerKey]);

  useEffect(() => {
    if (!isOtherRubroPickerCode(selectedCode)) {
      setOtherFeatureIds([]);
      setOtherRubroLabel('');
    }
  }, [selectedCode]);

  const canContinuePick = Boolean(selectedCode) && selectedCode !== currentCode && !loadingTypes;
  const canContinueOtherRubro =
    enteredViaOtherRubro && otherFeatureIds.length >= 1 && otherRubroLabel.trim().length >= 2;
  const blockersClear = blockers ? businessTypeChangeBlockersClear(blockers) : false;
  const canSubmit =
    understood &&
    blockersClear &&
    Boolean(resolvedTargetCode) &&
    !submitting &&
    !entLoading &&
    !blockersLoading;

  const targetDisplayLabel = enteredViaOtherRubro
    ? otherRubroLabel.trim() || resolvedMeta?.display_name || resolvedTargetCode || '—'
    : selectedMeta?.display_name ?? selectedCode ?? '—';

  const runChange = async (code: BusinessTypeCode) => {
    setSubmitting(true);
    try {
      const label = enteredViaOtherRubro ? otherRubroLabel.trim() : null;
      const result = await changeOrganizationBusinessType({
        newBusinessTypeCode: code,
        resetCatalog: true,
        applyNewPack: false,
        syncBranchDefaults: true,
        requestedVerticalLabel: label,
        updateRequestedVerticalLabel: true,
      });
      const toLabel = resolveOrganizationRubroDisplayLabel(result.businessProfile, pickerItems);
      if (enteredViaOtherRubro && otherRubroTemplate && label) {
        const userId = useAuthStore.getState().session?.user?.id;
        const orgId = entitlements?.organization_id;
        const branchId = useSessionStore.getState().currentBranchId ?? useSessionStore.getState().branches[0]?.id ?? null;
        if (userId && orgId) {
          try {
            await createOtherRubroSupportTicketIfNeeded({
              organizationId: orgId,
              branchId,
              userId,
              source: 'change_business_type_other',
              requestedVerticalLabel: label,
              operationTemplate: otherRubroTemplate,
              resolvedBusinessTypeCode: code,
              pickerBusinessTypeCode: OTHER_RUBRO_PICKER_CODE,
              selectedFeatureIds: otherFeatureIds,
            });
          } catch (ticketErr) {
            console.warn('[ChangeBusinessType] other rubro ticket failed', ticketErr);
          }
        }

        // #900 Phase 2: apply feature overrides for caps the new rubro doesn't have
        if (otherFeatureIds.length > 0 && entitlements?.organization_id) {
          try {
            const overrideResult = await applyOnboardingFeatureOverrides(
              entitlements.organization_id,
              otherFeatureIds,
            );
            if (overrideResult.applied.length > 0) {
              showSuccess(
                'Funciones extra activadas',
                `Sumamos ${overrideResult.applied.length} ${overrideResult.applied.length === 1 ? 'función que pediste' : 'funciones que pediste'} que no están en tu nuevo rubro.`,
                6000,
              );
            }
          } catch (overrideErr) {
            console.warn('[ChangeBusinessType] feature override failed', overrideErr);
          }
        }
      }
      showSuccess(
        'Rubro actualizado',
        `Ahora operás como ${toLabel}. El catálogo quedó vacío. Cargá lo que vendés.`,
        5200,
      );
      if (navigation.canGoBack()) {
        navigation.goBack();
      } else {
        navigateInShell(navigation, 'Organization');
      }
    } catch (e: unknown) {
      presentUnexpectedFailure({ module: 'settings', op: 'change_business_type', error: e, userMessage: 'No se pudo cambiar el rubro.' });
      void loadBlockers();
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (!resolvedTargetCode || !canSubmit) return;
    // Resolver rubro via RPC si hay label libre, fallback a template si falla.
    const finalCode = enteredViaOtherRubro && otherRubroTemplate && otherRubroLabel.trim()
      ? await resolveBusinessTypeFromLabelAndTemplate(otherRubroLabel.trim(), otherRubroTemplate)
      : resolvedTargetCode;
    void runChange(finalCode);
  };

  if (!multiBusinessEnabled || entitlements?.is_owner !== true) {
    return (
      <ModalScrollScreen variant="settingsWide" contentContainerStyle={styles.content} refreshing={refreshing} onRefresh={onRefresh}>
        <Text style={styles.blockedText}>El cambio de rubro no está disponible para esta cuenta.</Text>
      </ModalScrollScreen>
    );
  }

  const renderPickStep = () => (
    <View style={styles.stepBody}>
      <Text style={styles.nowPill}>
        Ahora: <Text style={styles.leadStrong}>{currentLabel}</Text>. Elegí otro.
      </Text>
      <BusinessTypePickerGrid
        businessTypes={pickerItems}
        selectedPickerKey={selectedPickerKey ?? undefined}
        onSelect={(item) => setSelectedPickerKey(item.picker_key)}
        layout={layout}
        loading={loadingTypes}
        disabled={submitting}
        currentCode={currentCode}
        searchable
        compact
      />
    </View>
  );

  const renderOtherRubroStep = () => (
    <OtherRubroStepPanel
      layout={layout}
      featureGroups={DEFAULT_OTHER_FEATURE_GROUPS}
      selectedFeatureIds={otherFeatureIds}
      onToggleFeature={(id) => setOtherFeatureIds((prev) => toggleOtherFeature(prev, id))}
      verticalLabel={otherRubroLabel}
      onChangeVerticalLabel={setOtherRubroLabel}
      disabled={submitting}
    />
  );

  const renderReviewStep = () => (
    <View style={styles.stepBody}>
      <BusinessTypeChangeImpactPanel
        fromLabel={currentLabel}
        toLabel={targetDisplayLabel}
        blockers={blockers}
        blockersLoading={blockersLoading}
        onGoCash={() => navigateInShell(navigation, 'CashManagement')}
        onGoOrders={() => navigateInShell(navigation, 'OrdersInbox')}
        onGoAppointments={() => navigateInShell(navigation, 'Appointments')}
        understood={understood}
        onUnderstoodChange={setUnderstood}
        disabled={submitting}
      />
    </View>
  );

  const goNextFromPick = () => {
    if (!canContinuePick) {
      if (selectedCode === currentCode) {
        showError('Mismo rubro', 'Elegí un rubro distinto al actual.');
      }
      return;
    }
    const next: Step = enteredViaOtherRubro ? 'other-rubro' : 'review';
    setStep(next);
    if (next === 'review') {
      void loadBlockers();
    }
  };

  const goNextFromOther = () => {
    if (!canContinueOtherRubro) {
      showError('Completá el detalle', 'Contanos qué vendés y marcá al menos una opción.');
      return;
    }
    setStep('review');
    void loadBlockers();
  };

  const flowSteps: Step[] = enteredViaOtherRubro
    ? ['pick', 'other-rubro', 'review']
    : ['pick', 'review'];
  const stepIndex = Math.max(0, flowSteps.indexOf(step));

  const stepTitle =
    step === 'pick'
      ? '¿A qué rubro pasás?'
      : step === 'other-rubro'
        ? 'Contanos un poco más'
        : 'Revisá y confirmá';

  const stepSubtitle =
    step === 'pick'
      ? undefined
      : step === 'other-rubro'
        ? 'Queda como Otros hasta que lo cataloguemos.'
        : 'Se borra el catálogo. Las ventas y las cuentas quedan.';

  return (
    <ModalScrollScreen
      variant="settingsWide"
      contentMaxWidth={layout.cardMaxWidth}
      contentContainerStyle={styles.content}
      refreshing={refreshing}
      onRefresh={onRefresh}
    >
      <ModalHeroHeader
        kicker="Organización"
        title={stepTitle}
        subtitle={stepSubtitle}
      />
      <View style={styles.steps}>
        {flowSteps.map((id, i) => (
          <View key={id} style={[styles.stepDot, i <= stepIndex && styles.stepDotOn]} />
        ))}
      </View>

      {step === 'pick' ? renderPickStep() : null}
      {step === 'other-rubro' ? renderOtherRubroStep() : null}
      {step === 'review' ? renderReviewStep() : null}

      <View style={styles.footer}>
        {step !== 'pick' ? (
          <TouchableOpacity
            style={styles.secondaryButton}
            onPress={() => setStep(step === 'review' && enteredViaOtherRubro ? 'other-rubro' : 'pick')}
            disabled={submitting}
            accessibilityRole="button"
          >
            <Text style={styles.secondaryButtonText}>Atrás</Text>
          </TouchableOpacity>
        ) : (
          <TouchableOpacity
            style={styles.secondaryButton}
            onPress={() => {
              if (navigation.canGoBack()) navigation.goBack();
              else navigateInShell(navigation, 'Organization');
            }}
            disabled={submitting}
            accessibilityRole="button"
          >
            <Text style={styles.secondaryButtonText}>Atrás</Text>
          </TouchableOpacity>
        )}
        {step === 'review' ? (
          <TouchableOpacity
            style={[styles.dangerButton, !canSubmit && styles.dangerButtonDisabled]}
            onPress={handleSubmit}
            disabled={!canSubmit}
            accessibilityRole="button"
            accessibilityLabel={`Pasar a ${targetDisplayLabel}`}
            accessibilityState={{ disabled: !canSubmit }}
            testID="change-rubro-submit"
          >
            {submitting ? (
              <ActivityIndicator color={ticketColors.paper} />
            ) : (
              <Text style={styles.dangerButtonText}>Pasar a {targetDisplayLabel}</Text>
            )}
          </TouchableOpacity>
        ) : (
          <TouchableOpacity
            style={[
              styles.primaryButton,
              ((step === 'pick' && !canContinuePick) ||
                (step === 'other-rubro' && !canContinueOtherRubro)) &&
                styles.primaryButtonDisabled,
            ]}
            onPress={step === 'pick' ? goNextFromPick : goNextFromOther}
            disabled={
              step === 'pick'
                ? !canContinuePick
                : step === 'other-rubro'
                  ? !canContinueOtherRubro
                  : submitting
            }
            accessibilityRole="button"
            testID="change-rubro-next"
          >
            <Text style={styles.primaryButtonText}>Siguiente</Text>
          </TouchableOpacity>
        )}
      </View>
    </ModalScrollScreen>
  );
};

const styles = StyleSheet.create({
  content: {
    paddingBottom: spacing.xxl,
    gap: spacing.lg,
  },
  stepBody: {
    gap: spacing.md,
  },
  steps: {
    flexDirection: 'row',
    gap: 6,
  },
  stepDot: {
    height: 4,
    flex: 1,
    borderRadius: 99,
    backgroundColor: ticketColors.ruleSoft,
  },
  stepDotOn: {
    backgroundColor: ticketColors.gold,
  },
  leadStrong: {
    color: ticketColors.ink,
    fontWeight: '600',
  },
  nowPill: {
    ...typography.caption,
    color: ticketColors.ink2,
    backgroundColor: ticketColors.paper2,
    borderRadius: 10,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  footer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  primaryButton: {
    flex: 1,
    backgroundColor: ticketColors.gold,
    borderRadius: 14,
    paddingHorizontal: spacing.lg,
    paddingVertical: 14,
    minWidth: 120,
    alignItems: 'center',
  },
  primaryButtonDisabled: {
    opacity: 0.45,
  },
  primaryButtonText: {
    ...typography.button,
    color: ticketColors.goldInk,
  },
  secondaryButton: {
    borderRadius: 14,
    paddingHorizontal: spacing.lg,
    paddingVertical: 14,
    minWidth: 88,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
    alignItems: 'center',
  },
  secondaryButtonText: {
    ...typography.button,
    color: ticketColors.ink,
  },
  dangerButton: {
    flex: 1,
    backgroundColor: ticketColors.bad,
    borderRadius: 14,
    paddingHorizontal: spacing.lg,
    paddingVertical: 14,
    minWidth: 160,
    alignItems: 'center',
    justifyContent: 'center',
  },
  dangerButtonDisabled: {
    opacity: 0.45,
  },
  dangerButtonText: {
    ...typography.button,
    color: ticketColors.paper,
  },
  blockedText: {
    ...typography.body,
    color: ticketColors.ink2,
  },
});
